import React, { useState, useEffect } from 'react';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const DateFormatTest = () => {
  const { 
    fetchAttendance, 
    exportToPDF, 
    exportToCSV,
    attendanceList,
    pagination,
    isLoading 
  } = useAttendance();
  
  const { user, isAuthenticated } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  const runDateFormatTests = async () => {
    setIsRunningTests(true);
    setTestResults([]);
    
    if (!isAuthenticated) {
      setTestResults([{
        name: 'Authentication Check',
        passed: false,
        message: 'User not authenticated. Please log in first.',
        timestamp: new Date().toISOString()
      }]);
      setIsRunningTests(false);
      toast.error('Please log in to run tests');
      return;
    }
    
    const tests = [
      {
        name: 'Test 1: Date Format Verification (July 2025)',
        test: async () => {
          try {
            const startDate = '2025-07-01';
            const endDate = '2025-07-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 10
            });
            
            const data = result.attendance || [];
            
            if (data.length === 0) {
              return {
                passed: false,
                message: 'No data to test date format',
                data: { recordCount: data.length }
              };
            }
            
            // Test the new date format
            const sampleRecord = data[0];
            const formattedDate = new Date(sampleRecord.stepIn).toLocaleDateString('en-US', { 
              day: '2-digit', 
              month: 'short', 
              year: 'numeric' 
            }).replace(',', '');
            
            // Check if format is correct (should be like "01 Jul 2025")
            const isCorrectFormat = /^\d{2} \w{3} \d{4}$/.test(formattedDate);
            
            return {
              passed: isCorrectFormat,
              message: `Date Format: ${formattedDate} (${isCorrectFormat ? 'CORRECT' : 'INCORRECT'})`,
              data: { 
                recordCount: data.length,
                formattedDate,
                isCorrectFormat,
                sampleDates: data.slice(0, 5).map(r => {
                  const date = new Date(r.stepIn);
                  return new Date(date).toLocaleDateString('en-US', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric' 
                  }).replace(',', '');
                })
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 2: PDF Export Date Format (August 2025)',
        test: async () => {
          try {
            const startDate = '2025-08-01';
            const endDate = '2025-08-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 5
            });
            
            const data = result.attendance || [];
            
            if (data.length === 0) {
              return {
                passed: false,
                message: 'No data to test PDF date format',
                data: { recordCount: data.length }
              };
            }
            
            // Test PDF export with new date format
            const startTime = Date.now();
            await exportToPDF(data, `test_date_format_august_${Date.now()}.pdf`);
            const endTime = Date.now();
            
            // Check date format in the data
            const formattedDates = data.map(record => {
              return new Date(record.stepIn).toLocaleDateString('en-US', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              }).replace(',', '');
            });
            
            const allCorrectFormat = formattedDates.every(date => /^\d{2} \w{3} \d{4}$/.test(date));
            
            return {
              passed: allCorrectFormat,
              message: `PDF Date Format: ${allCorrectFormat ? 'CORRECT' : 'INCORRECT'} - ${formattedDates.join(', ')} (${endTime - startTime}ms)`,
              data: { 
                recordCount: data.length,
                formattedDates,
                allCorrectFormat,
                exportTime: endTime - startTime
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `PDF export failed: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 3: CSV Export Date Format (September 2025)',
        test: async () => {
          try {
            const startDate = '2025-09-01';
            const endDate = '2025-09-30';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 5
            });
            
            const data = result.attendance || [];
            
            if (data.length === 0) {
              return {
                passed: false,
                message: 'No data to test CSV date format',
                data: { recordCount: data.length }
              };
            }
            
            // Test CSV export with new date format
            const startTime = Date.now();
            await exportToCSV(data, `test_date_format_september_${Date.now()}.csv`);
            const endTime = Date.now();
            
            // Check date format in the data
            const formattedDates = data.map(record => {
              return new Date(record.stepIn).toLocaleDateString('en-US', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              }).replace(',', '');
            });
            
            const allCorrectFormat = formattedDates.every(date => /^\d{2} \w{3} \d{4}$/.test(date));
            
            return {
              passed: allCorrectFormat,
              message: `CSV Date Format: ${allCorrectFormat ? 'CORRECT' : 'INCORRECT'} - ${formattedDates.join(', ')} (${endTime - startTime}ms)`,
              data: { 
                recordCount: data.length,
                formattedDates,
                allCorrectFormat,
                exportTime: endTime - startTime
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `CSV export failed: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 4: Date Format Examples',
        test: async () => {
          try {
            // Test various date formats to ensure consistency
            const testDates = [
              new Date('2025-07-01'),
              new Date('2025-07-15'),
              new Date('2025-07-31'),
              new Date('2025-08-01'),
              new Date('2025-12-25')
            ];
            
            const formattedDates = testDates.map(date => {
              return date.toLocaleDateString('en-US', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              }).replace(',', '');
            });
            
            const allCorrectFormat = formattedDates.every(date => /^\d{2} \w{3} \d{4}$/.test(date));
            
            return {
              passed: allCorrectFormat,
              message: `Date Format Examples: ${allCorrectFormat ? 'CORRECT' : 'INCORRECT'} - ${formattedDates.join(', ')}`,
              data: { 
                formattedDates,
                allCorrectFormat,
                expectedFormat: 'DD MMM YYYY (e.g., 01 Jul 2025)'
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      }
    ];

    const results = [];
    
    for (const test of tests) {
      try {
        console.log(`🧪 Running: ${test.name}`);
        const result = await test.test();
        results.push({
          ...result,
          name: test.name,
          timestamp: new Date().toISOString()
        });
        
        console.log(`✅ ${test.name}: ${result.message}`);
      } catch (error) {
        console.error(`❌ ${test.name}: ${error.message}`);
        results.push({
          name: test.name,
          passed: false,
          message: `Error: ${error.message}`,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    setTestResults(results);
    setIsRunningTests(false);
    
    const passedTests = results.filter(r => r.passed).length;
    const totalTests = results.length;
    
    if (passedTests === totalTests) {
      toast.success(`All ${totalTests} date format tests passed! 🎉`);
    } else {
      toast.error(`${passedTests}/${totalTests} tests passed`);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-2xl font-bold mb-4">📅 Date Format Test Suite</h2>
      
      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h3 className="font-semibold text-green-800 mb-2">🎯 Testing Date Format Changes:</h3>
        <ul className="text-sm text-green-700 space-y-1">
          <li>• <strong>Before:</strong> 07/01/2025 (confusing - could be July 1st or January 7th)</li>
          <li>• <strong>After:</strong> 01 Jul 2025 (clear - definitely July 1st)</li>
          <li>• <strong>Format:</strong> DD MMM YYYY (e.g., 01 Jul 2025, 15 Aug 2025)</li>
          <li>• <strong>Benefits:</strong> No ambiguity, internationally clear, professional</li>
        </ul>
      </div>
      
      <div className="mb-6">
        <button
          onClick={runDateFormatTests}
          disabled={isRunningTests}
          className="btn btn-primary"
        >
          {isRunningTests ? 'Running Tests...' : 'Run Date Format Tests'}
        </button>
      </div>

      {/* Current Data Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <h3 className="font-semibold">Authentication</h3>
          <p className="text-sm text-gray-600">
            Status: {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
          </p>
          <p className="text-sm text-gray-600">
            User: {user?.name || 'N/A'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Current Data</h3>
          <p className="text-sm text-gray-600">
            Records: {attendanceList.length}
          </p>
          <p className="text-sm text-gray-600">
            Total: {pagination.totalRecords}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Pagination</h3>
          <p className="text-sm text-gray-600">
            Page: {pagination.currentPage}/{pagination.totalPages}
          </p>
          <p className="text-sm text-gray-600">
            Limit: {pagination.limit}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Status</h3>
          <p className="text-sm text-gray-600">
            Loading: {isLoading ? 'Yes' : 'No'}
          </p>
          <p className="text-sm text-gray-600">
            Tests: {testResults.length}
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          {testResults.map((result, index) => (
            <div key={index} className={`card p-4 ${result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">{result.name}</h4>
                  <p className="text-sm text-gray-600">{result.message}</p>
                  {result.data && (
                    <div className="mt-2 text-xs text-gray-500">
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
                <div className={`badge ${result.passed ? 'badge-success' : 'badge-error'}`}>
                  {result.passed ? 'PASS' : 'FAIL'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateFormatTest;

